/**
 * Screen 3 — Invoice Detail.
 *
 * A real route (`/project-controls/cost/invoices/:invoiceId?tab=…`) rather than
 * a slide-over: acceptance criterion 7 is a direct-URL test, the content does
 * not fit a panel, and people paste invoice links to each other in meetings.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, ChevronRight, CircleDot, Clock, FileText,
  Gavel, Landmark, Loader2, Paperclip, Receipt, Scale, Shield, SplitSquareHorizontal,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import {
  MAIN_STAGES, PARTY, STAGE_LABEL, day, int, money, num, stamp, toneColor,
  useIptPrefs, useIptTheme,
} from './iptTheme';
import {
  AxisLegend, Bar, Btn, Def, Empty, Field, Input, Panel, Sample, Select, StageChip,
  Tile, WaitingPill,
} from './IptPrimitives';
import { IptPrefsBar } from './IptPrefsBar';
import { IptDocViewer } from './IptDocViewer';
import { AnalyseChip, MatchSuggestions, useAnalyse } from './IptMatchSuggestions';

const TABS = [
  ['lifecycle', 'Lifecycle', CircleDot],
  ['match', 'Three-way match', SplitSquareHorizontal],
  ['checklist', 'Verification', Shield],
  ['certification', 'Certification', CheckCircle2],
  ['deductions', 'Deductions & retention', Landmark],
  ['payments', 'Payments', Receipt],
  ['documents', 'Documents', Paperclip],
  ['audit', 'Audit trail', Scale],
];

export const IptDetail = () => {
  const T = useIptTheme();
  const prefs = useIptPrefs();
  const { invoiceId } = useParams();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const tab = TABS.some(([k]) => k === sp.get('tab')) ? sp.get('tab') : 'lifecycle';
  const [d, setD] = useState(null);
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);
  const [move, setMove] = useState(null);

  const load = useCallback(() => {
    setErr(null);
    api.get(`/ipt/invoices/${invoiceId}?${prefs.query}`)
      .then((r) => setD(r.data))
      .catch((e) => setErr(e?.response?.status === 404
        ? 'This invoice does not exist, or it is outside your access.'
        : (e?.response?.data?.detail || 'Could not load this invoice')));
  }, [invoiceId, prefs.query]);
  useEffect(load, [load]);
  useEffect(() => { api.get('/ipt/meta').then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const setTab = (k) => {
    const next = new URLSearchParams(sp);
    next.set('tab', k);
    setSp(next, { replace: true });
  };

  if (err) {
    return (
      <DashboardLayout>
        <div className="py-24 text-center space-y-3" data-testid="ipt-detail-error">
          <AlertTriangle className="w-6 h-6 mx-auto" style={{ color: T.amber }} />
          <p className="text-[13px]" style={{ color: T.body }}>{err}</p>
          <Btn T={T} variant="ghost" testId="ipt-detail-back-error"
            onClick={() => navigate('/project-controls/cost/invoices?tab=register')}>
            Back to the register
          </Btn>
        </div>
      </DashboardLayout>
    );
  }
  if (!d) {
    return (
      <DashboardLayout>
        <div className="py-28 text-center" data-testid="ipt-detail-loading">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: T.primary }} />
        </div>
      </DashboardLayout>
    );
  }

  const inv = d.invoice;
  const m = d.metrics || {};
  const defs = d.definitions || {};
  const cur = inv.currency || 'INR';
  const vim = meta?.vimSource;
  const fromVim = inv.received_via === 'SAP VIM' || inv.origin === 'sap_vim';

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="ipt-detail" style={{ fontFamily: 'Inter, sans-serif' }}>
        {/* ── header ───────────────────────────────────────────────── */}
        <header className="sticky top-14 z-20 -mx-6 px-6 py-3"
          style={{ background: T.page, borderBottom: `1px solid ${T.border}` }}>
          <button type="button" data-testid="ipt-detail-back"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 text-[10.5px] mb-1 hover:underline"
            style={{ color: T.muted }}>
            <ArrowLeft className="w-3 h-3" /> back to the register
            <ChevronRight className="w-3 h-3" />
            <span style={{ color: T.body }}>{inv.project_id}</span>
          </button>
          <div className="flex items-start gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-[21px] font-bold leading-tight"
                  style={{ color: T.ink, fontFamily: '"Space Grotesk", Inter, sans-serif' }}
                  data-testid="ipt-detail-invoice-no">{inv.invoice_no}</h1>
                <StageChip stage={inv.current_stage} T={T} testId="ipt-detail-stage" />
                {inv.imported_without_history && (
                  <span className="px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase"
                    data-testid="ipt-detail-imported"
                    style={{ background: T.tint(T.amber, '1F'), color: T.amber }}>
                    imported — no stage history
                  </span>
                )}
                {fromVim && vim?.simulated && (
                  <span className="px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase"
                    data-testid="ipt-detail-vim-sim"
                    style={{ background: T.tint(T.accent, '1F'), color: T.accent }}>
                    SAP VIM: simulated source
                  </span>
                )}
              </div>
              <p className="text-[11.5px] mt-0.5" style={{ color: T.body }}>
                {inv.vendor_name} · vendor ref {inv.vendor_invoice_no || '—'} ·
                {' '}{inv.po_reference} · WBS {inv.wbs_element}
                {inv.cc_contract_id && (
                  <>
                    {' · '}
                    <a href={`/contracts/${inv.cc_contract_id}`} data-testid="ipt-cc-link"
                      className="hover:underline" style={{ color: T.primary }}
                      title="Open this vendor's contract in Contracts & Claims">
                      contract {inv.contract_id || inv.cc_contract_number}
                    </a>
                  </>
                )}
              </p>
            </div>
            <div className="ml-auto flex items-center gap-2 flex-wrap">
              <IptPrefsBar compact />
              {(d.nextStages || []).length > 0 && !meta?.principal?.isVendor && (
                <Btn T={T} testId="ipt-detail-move-btn"
                  onClick={() => setMove({ to_stage: '', waiting_on: '', dispute_basis: '', reason: '' })}>
                  Move stage
                </Btn>
              )}
            </div>
          </div>
          {inv.is_blocked && (
            <div className="mt-2.5 px-3 py-2 rounded-md text-[11.5px] flex items-start gap-2"
              data-testid="ipt-detail-block-banner"
              style={{ background: T.tint(T.red, '14'), color: T.red,
                       border: `1px solid ${T.tint(T.red, '3A')}` }}>
              <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
              <span><b>Release blocked.</b> {inv.block_reason}</span>
            </div>
          )}
          <nav className="flex items-center gap-1 mt-3 flex-wrap">
            {TABS.map(([key, label, Icon]) => (
              <button key={key} type="button" onClick={() => setTab(key)}
                data-testid={`ipt-detail-tab-${key}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold"
                style={tab === key
                  ? { background: T.primary, color: '#fff' }
                  : { background: 'transparent', color: T.body, border: `1px solid ${T.border}` }}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </nav>
        </header>

        {/* ── money + clock tiles ─────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <Tile T={T} testId="ipt-detail-gross" label="Gross" value={money(inv.gross_amount, cur)}
            sub={`invoice dated ${day(inv.invoice_date)}`}
            def={{ title: 'Gross amount', body: 'As claimed by the vendor, before deductions.' }} />
          <Tile T={T} testId="ipt-detail-net" label="Net payable" value={money(inv.net_payable, cur)}
            sub={`${int((d.deductions || []).length)} deductions applied`}
            def={{ title: 'Net payable',
              body: 'Gross less every applied deduction — retention, advance recovery, '
                 + 'tax withholding, liquidated damages and penalties.' }} />
          <Tile T={T} testId="ipt-detail-ageing"
            label={inv.current_stage === 'PAID' ? 'Days to payment' : 'Ageing'}
            value={inv.current_stage === 'PAID'
              ? `${num(m.days_to_payment, 1)} d` : `${num(m.ageing_days, 1)} d`}
            tone={m.is_overdue ? T.red : undefined}
            sub={inv.current_stage === 'PAID'
              ? `net of the clock: ${num(m.days_to_payment_net, 1)} d`
              : `bucket ${m.ageing_bucket || '—'}`}
            def={{ title: inv.current_stage === 'PAID' ? 'Days to payment' : 'Ageing',
              body: inv.current_stage === 'PAID'
                ? (defs.days_to_payment || {}).basis : (defs.ageing || {}).basis,
              basis: m.basis }} />
          <Tile T={T} testId="ipt-detail-beyond" label="Beyond terms"
            value={m.days_beyond_terms > 0 ? `${num(m.days_beyond_terms, 1)} d` : 'within terms'}
            tone={m.days_beyond_terms > 0 ? T.red : T.green}
            sub={`due ${day(inv.due_date)} · ${int(inv.payment_terms_days)} day terms`}
            def={{ title: 'Beyond terms', body: (defs.terms || {}).basis, basis: m.basis }} />
          <Tile T={T} testId="ipt-detail-cycle" label="Cycle time"
            value={`${num(m.grossCycleDays, 1)} d`}
            sub={`net ${num(m.netCycleDays, 1)} d · clock stopped ${num(m.stopClockDays, 1)} d`}
            def={{ title: 'Gross and net cycle time',
              body: (defs.stop_clock || {}).note, basis: m.basis,
              axis: 'suspension', version: (defs.stop_clock || {}).version }} />
          <Tile T={T} testId="ipt-detail-retention" label="Retention on this contract"
            value={money((d.retention || []).reduce((a, r) => a + (r.balance_held || 0), 0), cur)}
            sub={`${int((d.retention || []).length)} balances for this vendor`}
            def={{ title: 'Retention held',
              body: 'Sum of the Retention deductions actually applied to this vendor on '
                 + 'this project, less recorded releases.' }} />
        </div>

        <div className="pb-12">
          {tab === 'lifecycle' && <Lifecycle d={d} T={T} defs={defs} vim={vim} />}
          {tab === 'match' && <ThreeWay d={d} T={T} cur={cur} onReload={load} />}
          {tab === 'checklist' && <Checklist d={d} T={T} onChanged={load} />}
          {tab === 'certification' && <Certification d={d} T={T} cur={cur} />}
          {tab === 'deductions' && <Deductions d={d} T={T} cur={cur} />}
          {tab === 'payments' && <Payments d={d} T={T} cur={cur} vim={vim} />}
          {tab === 'documents' && <Documents d={d} T={T} onReload={load} />}
          {tab === 'audit' && <Audit d={d} T={T} vim={vim} />}
        </div>

        {move && (
          <MoveDialog T={T} d={d} meta={meta} state={move} setState={setMove}
            onDone={() => { setMove(null); load(); }} />
        )}
      </div>
    </DashboardLayout>
  );
};

/* ── lifecycle tab ───────────────────────────────────────────────────── */
const Lifecycle = ({ d, T, defs, vim }) => {
  const m = d.metrics || {};
  const inv = d.invoice;
  const reached = useMemo(() => new Set((d.transitions || []).map((t) => t.to_stage)), [d]);
  const a = m.attribution || {};
  const segs = [
    { label: 'client', value: a.clientDays, colour: toneColor(T, PARTY.CLIENT.tone) },
    { label: 'vendor', value: a.vendorDays, colour: toneColor(T, PARTY.VENDOR.tone) },
    { label: 'neither party', value: a.neitherDays, colour: T.muted },
  ];

  return (
    <div className="space-y-4">
      <Panel T={T} testId="ipt-lifecycle-tracker" title="Lifecycle"
        subtitle="Seven mainline stages. Branches are shown where the invoice took one.">
        <div className="flex items-stretch gap-1 overflow-x-auto pb-1">
          {MAIN_STAGES.map((s, i) => {
            const done = reached.has(s);
            const here = inv.current_stage === s;
            const c = here ? T.primary : (done ? T.green : T.border);
            const dwell = (m.stageDwellDays || {})[s];
            return (
              <div key={s} className="min-w-[116px] flex-1 rounded-md px-2.5 py-2"
                data-testid={`ipt-track-${s}`}
                style={{ background: here ? T.tint(T.primary, '14') : T.surface,
                         border: `1px solid ${here ? T.tint(T.primary, '55') : T.border}`,
                         borderLeft: `3px solid ${c}` }}>
                <div className="text-[9px] uppercase tracking-wider" style={{ color: T.muted }}>
                  {i + 1} of 7
                </div>
                <div className="text-[11px] font-semibold leading-tight mt-0.5"
                  style={{ color: done || here ? T.ink : T.muted }}>{STAGE_LABEL[s]}</div>
                <div className="text-[10px] tabular-nums mt-1" style={{ color: T.body }}>
                  {dwell === undefined ? '—' : `${num(dwell, 1)} d`}
                </div>
              </div>
            );
          })}
        </div>
        {['RETURNED', 'ON_HOLD', 'DISPUTED', 'PARTIALLY_PAID', 'REJECTED']
          .filter((s) => reached.has(s)).length > 0 && (
          <div className="flex items-center gap-2 mt-2.5 flex-wrap">
            <span className="text-[10px]" style={{ color: T.muted }}>Branches taken:</span>
            {['RETURNED', 'ON_HOLD', 'DISPUTED', 'PARTIALLY_PAID', 'REJECTED']
              .filter((s) => reached.has(s)).map((s) => (
                <span key={s} className="flex items-center gap-1">
                  <StageChip stage={s} T={T} testId={`ipt-branch-${s}`} />
                  <span className="text-[10px] tabular-nums" style={{ color: T.body }}>
                    {num((m.stageDwellDays || {})[s], 1)} d
                  </span>
                </span>
              ))}
          </div>
        )}
      </Panel>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-4">
        <Panel T={T} testId="ipt-transitions" title="Stage history"
          subtitle={`${int((d.transitions || []).length)} transitions — the single source of truth`}
          right={<Def T={T} testId="ipt-transitions-def" title="Stage history"
            body={"Every stage change is one row, written by the stage machine. Durations are recomputed from these timestamps on every read, so a backdated correction changes history without rewriting the present."}
            basis={m.basis} />}>
          <ol className="space-y-0">
            {(d.transitions || []).map((t, i) => {
              const inferred = t.waiting_on_source === 'inferred';
              return (
                <li key={t.transition_id} data-testid={`ipt-transition-${i}`}
                  className="flex gap-3 py-2"
                  style={{ borderTop: i ? `1px solid ${T.border}` : 'none' }}>
                  <div className="w-[108px] shrink-0 text-[10px] tabular-nums"
                    style={{ color: T.muted }}>{stamp(t.occurred_on)}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {t.from_stage && (
                        <>
                          <span className="text-[10px]" style={{ color: T.muted }}>
                            {STAGE_LABEL[t.from_stage] || t.from_stage}
                          </span>
                          <ChevronRight className="w-3 h-3" style={{ color: T.muted }} />
                        </>
                      )}
                      <StageChip stage={t.to_stage} T={T} />
                      <WaitingPill party={t.waiting_on} inferred={inferred} T={T}
                        testId={`ipt-waiting-${i}`} />
                      {t.is_stop_clock && (
                        <span className="px-1.5 py-px rounded text-[9px] font-bold uppercase tracking-wide"
                          style={{ background: T.tint(T.primaryLight, '1A'), color: T.primaryLight }}>
                          clock suspended
                        </span>
                      )}
                      {t.source === 'SAP_VIM' && vim?.simulated && (
                        <span className="px-1.5 py-px rounded text-[9px] font-bold uppercase"
                          style={{ background: T.tint(T.accent, '1A'), color: T.accent }}>
                          VIM simulated
                        </span>
                      )}
                    </div>
                    {t.dispute_basis && (
                      <p className="text-[10.5px] mt-0.5 italic" style={{ color: T.body }}>
                        “{t.dispute_basis}”
                      </p>
                    )}
                    {t.reason && t.reason !== t.dispute_basis && (
                      <p className="text-[10.5px] mt-0.5" style={{ color: T.body }}>{t.reason}</p>
                    )}
                    <p className="text-[9.5px] mt-0.5" style={{ color: T.muted }}>
                      {t.actor_type} · {t.actor_id} · {t.actor_role}
                      {t.duration_in_previous_stage_hours !== null
                        && t.duration_in_previous_stage_hours !== undefined
                        && ` · after ${num(t.duration_in_previous_stage_hours / 24, 1)} d in the previous stage`}
                      {t.correlation_id && ` · corr ${t.correlation_id}`}
                    </p>
                  </div>
                </li>
              );
            })}
            {!(d.transitions || []).length && <Empty T={T}>No stage history.</Empty>}
          </ol>
        </Panel>

        <div className="space-y-4">
          <Panel T={T} testId="ipt-attribution" title="Where the time went"
            subtitle="Two independent axes"
            right={<Def T={T} testId="ipt-attribution-def" title="Delay attribution"
              body={(defs.delay_attribution || {}).rule}
              basis={m.basis}
              sample={`${int(a.declaredSpans)} of ${int(a.spans)} periods carry a recorded party; `
                + `${int(a.inferredSpans)} were reconstructed and are shown as unknown.`}
              axis="attribution" version={(defs.delay_attribution || {}).version} />}>
            <Bar T={T} height={14} testId="ipt-attribution-bar" segments={segs} />
            <div className="mt-2.5 space-y-1.5">
              {[['CLIENT', a.clientDays], ['VENDOR', a.vendorDays], ['NEITHER', a.neitherDays]]
                .map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 text-[11px]">
                    <span className="w-2.5 h-2.5 rounded-sm"
                      style={{ background: toneColor(T, PARTY[k].tone) }} />
                    <span style={{ color: T.body }}>{PARTY[k].label}</span>
                    <span className="ml-auto tabular-nums font-semibold" style={{ color: T.ink }}>
                      {num(v, 1)} d
                    </span>
                  </div>
                ))}
              {a.inferredDays > 0 && (
                <div className="flex items-center gap-2 text-[11px]" data-testid="ipt-attr-inferred">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{
                    background: `repeating-linear-gradient(135deg, ${T.muted} 0 2px, transparent 2px 4px)`,
                    border: `1px dashed ${T.borderStrong}` }} />
                  <span style={{ color: T.muted }}>of which party unknown</span>
                  <span className="ml-auto tabular-nums" style={{ color: T.muted }}>
                    {num(a.inferredDays, 1)} d
                  </span>
                </div>
              )}
            </div>
            <Sample T={T} testId="ipt-attribution-sum"
              text={a.sumsToGross
                ? `The three parties account for the full ${num(m.grossCycleDays, 1)} ${m.basis} — every period belongs to exactly one party.`
                : 'Warning: the attribution buckets do not sum to the gross cycle time.'} />
          </Panel>

          <Panel T={T} testId="ipt-clock" title="Clock" subtitle="Contractual suspension">
            <div className="space-y-1.5 text-[11px]">
              <KV T={T} k="Gross cycle" v={`${num(m.grossCycleDays, 1)} d`} />
              <KV T={T} k="Clock suspended" v={`${num(m.stopClockDays, 1)} d`} tone={T.primaryLight} />
              <KV T={T} k="Net cycle" v={`${num(m.netCycleDays, 1)} d`} tone={T.ink} bold />
              <KV T={T} k="Stop clock" v={m.stopClockEnabled ? 'applied' : 'switched off'} />
            </div>
            <Sample T={T} text={(defs.stop_clock || {}).note} />
          </Panel>

          <AxisLegend axes={{ suspension: defs.stop_clock, attribution: defs.delay_attribution,
            note: (defs.axes || {}).note }} T={T} />
        </div>
      </div>
    </div>
  );
};

/* ── three-way match ─────────────────────────────────────────────────── */
const ThreeWay = ({ d, T, cur, onReload }) => {
  const tw = d.threeWay || {};
  const tol = tw.tolerance || {};
  return (
    <div className="space-y-4">
    <Panel T={T} testId="ipt-threeway" title="Three-way match"
      subtitle={`PO vs SEN/GRN vs invoice, per line · tolerance ${num(tol.qtyPct, 1)}% quantity, ${num(tol.valuePct, 1)}% value`}
      right={(
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase"
            data-testid="ipt-threeway-verdict"
            style={{ background: T.tint(tw.pass ? T.green : T.red, '1F'),
                     color: tw.pass ? T.green : T.red }}>
            {tw.pass ? 'within tolerance' : 'out of tolerance'}
          </span>
          <Def T={T} testId="ipt-threeway-def" title="Three-way match"
            body={"Invoice quantity and value are compared against the goods-receipt (SEN/GRN) quantity, with the purchase order shown as the authorised ceiling. A line fails when the variance against the receipt exceeds the configured tolerance."}
            sample={`${int((tw.rows || []).length)} lines compared. Quantity ${tw.qtyPass ? 'passes' : 'fails'}; value ${tw.valuePass ? 'passes' : 'fails'}.`} />
        </div>
      )}>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]" data-testid="ipt-threeway-table">
          <thead>
            <tr style={{ color: T.muted, background: T.surfaceAlt }}>
              {['#', 'Description', 'PO qty', 'GRN qty', 'Measured qty', 'Invoice qty',
                'Qty var', 'PO value', 'GRN value', 'Invoice value', 'Value var', 'Result']
                .map((h, i) => (
                  <th key={h} className={`py-2 px-2 font-semibold whitespace-nowrap ${i >= 2 ? 'text-right' : 'text-left'}`}>
                    {h}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {(tw.rows || []).map((r) => {
              const ok = r.qtyPass && r.valuePass;
              return (
                <tr key={r.line_no} data-testid={`ipt-threeway-row-${r.line_no}`}
                  style={{ borderTop: `1px solid ${T.border}` }}>
                  <td className="py-1.5 px-2 tabular-nums" style={{ color: T.muted }}>{r.line_no}</td>
                  <td className="py-1.5 px-2" style={{ color: T.ink }}>
                    {r.description}
                    <div className="text-[9.5px] font-mono" style={{ color: T.muted }}>
                      {r.po_ref} · {r.grn_ref} · {r.jmr_ref}
                    </div>
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums" style={{ color: T.body }}>
                    {num(r.po_quantity, 2)} {r.uom}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums" style={{ color: T.body }}>
                    {num(r.grn_quantity, 2)}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums"
                    data-testid={`ipt-threeway-measured-${r.line_no}`}
                    style={{ color: r.measured_quantity === null ? T.muted : T.ink }}
                    title={r.measuredSourceFile
                      ? `Accepted from ${r.measuredSourceFile}` : undefined}>
                    {r.measured_quantity === null ? '—' : num(r.measured_quantity, 2)}
                    {r.measuredVariancePct !== null && r.measuredVariancePct !== undefined && (
                      <span className="block text-[9px]"
                        style={{ color: Math.abs(r.measuredVariancePct) > (tol.qtyPct || 2)
                          ? T.red : T.muted }}>
                        {num(r.measuredVariancePct, 2)}% vs claim
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums font-semibold"
                    style={{ color: T.ink }}>{num(r.invoice_quantity, 2)}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums"
                    style={{ color: r.qtyPass ? T.muted : T.red }}>
                    {r.qtyVariancePct === null ? '—' : `${num(r.qtyVariancePct, 2)}%`}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums" style={{ color: T.body }}>
                    {money(r.po_value, cur)}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums" style={{ color: T.body }}>
                    {money(r.grn_value, cur)}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums font-semibold"
                    style={{ color: T.ink }}>{money(r.invoice_value, cur)}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums"
                    style={{ color: r.valuePass ? T.muted : T.red }}>
                    {r.valueVariancePct === null ? '—' : `${num(r.valueVariancePct, 2)}%`}
                  </td>
                  <td className="py-1.5 px-2 text-right">
                    <span className="px-1.5 py-px rounded text-[9px] font-bold uppercase"
                      style={{ background: T.tint(ok ? T.green : T.red, '1A'),
                               color: ok ? T.green : T.red }}>{ok ? 'pass' : 'fail'}</span>
                  </td>
                </tr>
              );
            })}
            {!(tw.rows || []).length && <tr><td colSpan={12}><Empty T={T}>No lines on this invoice.</Empty></td></tr>}
          </tbody>
        </table>
      </div>
      <Sample T={T} testId="ipt-threeway-sample"
        text={`Quantity variance is measured against the goods receipt, not the purchase order: `
          + `the PO is the authorisation, the receipt is what actually arrived. Tolerances are `
          + `configurable per project (currently ${num(tol.qtyPct, 1)}% on quantity, ${num(tol.valuePct, 1)}% on value). `
          + (tw.measuredNote || 'The measured column fills only from an accepted reading of an attachment.')} />
    </Panel>
    <MatchSuggestions d={d} T={T} onReload={onReload} />
    </div>
  );
};

/* ── remaining tabs ──────────────────────────────────────────────────── */
const Checklist = ({ d, T, onChanged }) => {
  const [busy, setBusy] = useState(null);
  const gate = d.releaseGate || {};
  const groups = useMemo(() => {
    const g = {};
    (d.checklist || []).forEach((c) => { (g[c.category] = g[c.category] || []).push(c); });
    return g;
  }, [d]);
  const setResult = async (code, result) => {
    setBusy(code);
    try {
      await api.put(`/ipt/invoices/${d.invoice.invoice_id}/checklist/${code}`, { result });
      toast.success('Verification item updated');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update the item'); }
    finally { setBusy(null); }
  };
  const TONE = { PASS: T.green, FAIL: T.red, NA: T.muted, WAIVED: T.amber, PENDING: T.muted };
  return (
    <Panel T={T} testId="ipt-checklist" title="Finance verification checklist"
      subtitle={gate.ok ? 'Release gate open' : `Release blocked — ${gate.blockReason || ''}`}
      right={<Def T={T} testId="ipt-checklist-def" title="Release gate"
        body={"A structured checklist decides release, not a free-text override. Any release-blocking item that has failed, or has not yet been performed, keeps the gate shut and the invoice blocked."}
        sample={`${int((d.checklist || []).length)} items · ${int((gate.failed || []).length)} failed · ${int((gate.pending || []).length)} release-blocking checks not yet performed.`} />}>
      <div className="space-y-3">
        {Object.entries(groups).map(([cat, items]) => (
          <div key={cat}>
            <div className="text-[9.5px] uppercase tracking-[0.09em] mb-1" style={{ color: T.muted }}>
              {cat}
            </div>
            <div className="space-y-1">
              {items.map((c) => (
                <div key={c.item_code} data-testid={`ipt-check-${c.item_code}`}
                  className="flex items-start gap-2 px-2 py-1.5 rounded-md"
                  style={{ background: T.surfaceAlt, border: `1px solid ${T.border}` }}>
                  <span className="text-[11px] flex-1" style={{ color: T.ink }}>
                    {c.item_label}
                    <span className="ml-1.5 text-[9px] uppercase" style={{ color: T.muted }}>
                      {c.is_mandatory ? 'mandatory' : 'optional'}
                      {c.blocks_release ? ' · blocks release' : ''}
                      {c.source_module ? ` · from ${c.source_module}` : ''}
                    </span>
                    {c.comment && (
                      <span className="block text-[10px] mt-0.5" style={{ color: T.red }}>
                        {c.comment}
                      </span>
                    )}
                  </span>
                  <select value={c.result || 'PENDING'} disabled={busy === c.item_code}
                    data-testid={`ipt-check-select-${c.item_code}`}
                    onChange={(e) => setResult(c.item_code, e.target.value)}
                    className="text-[10px] rounded px-1.5 py-1 outline-none font-semibold"
                    style={{ background: T.surface, color: TONE[c.result] || T.body,
                             border: `1px solid ${T.border}` }}>
                    {['PENDING', 'PASS', 'FAIL', 'NA', 'WAIVED'].map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
};

const Certification = ({ d, T, cur }) => (
  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
    <Panel T={T} testId="ipt-certifications" title="Certification"
      subtitle="Technical and quantity certification against the Joint Measurement Sheet">
      {(d.certifications || []).length ? (d.certifications || []).map((c) => (
        <div key={c.certification_id} className="space-y-1 text-[11px]"
          data-testid={`ipt-cert-${c.certification_id}`}>
          <KV T={T} k="Type" v={c.cert_type} />
          <KV T={T} k="Certified by" v={`${c.certified_by_name || c.certified_by} · ${day(c.certified_on)}`} />
          <KV T={T} k="JMR reference" v={c.jmr_reference} />
          <KV T={T} k="Certified amount" v={money(c.certified_amount, cur)} bold />
          <KV T={T} k="Variance vs claimed" v={money(c.variance_vs_claimed, cur)}
            tone={c.variance_vs_claimed < 0 ? T.red : T.green} />
          {c.remarks && <p className="text-[10.5px] pt-1" style={{ color: T.body }}>{c.remarks}</p>}
        </div>
      )) : <Empty T={T}>Not yet certified.</Empty>}
    </Panel>
    <div className="space-y-4">
      <Panel T={T} testId="ipt-securities" title="Securities"
        subtitle="Bank guarantees and insurance, read from the securities module">
        {(d.securities?.items || d.securities?.rows || []).length
          ? (d.securities.items || d.securities.rows).map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-[11px] py-1"
              style={{ borderTop: i ? `1px solid ${T.border}` : 'none' }}>
              <span style={{ color: T.ink }}>{s.security_type}</span>
              <span className="font-mono text-[10px]" style={{ color: T.muted }}>{s.reference}</span>
              <span className="ml-auto tabular-nums" style={{ color: T.body }}>
                {money(s.amount, cur)}
              </span>
              <span className="tabular-nums text-[10px]"
                style={{ color: new Date(s.valid_until) < new Date() ? T.red : T.green }}>
                {day(s.valid_until)}
              </span>
            </div>
          ))
          : <Empty T={T}>{d.securities?.statement || 'No securities on file.'}</Empty>}
      </Panel>
      <Panel T={T} testId="ipt-nfa" title="Approval reference (NFA)"
        subtitle="The governance reference authorising the commitment">
        <pre className="text-[10px] leading-relaxed overflow-x-auto"
          style={{ color: T.body }}>{JSON.stringify(d.nfaReference ?? null, null, 2)}</pre>
      </Panel>
    </div>
  </div>
);

const Deductions = ({ d, T, cur }) => (
  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
    <Panel T={T} testId="ipt-deductions" title="Deductions"
      subtitle={`${int((d.deductions || []).length)} applied to this invoice`}
      right={<Def T={T} testId="ipt-deductions-def" title="Deductions"
        body={"Retention, advance recovery, tax withholding, liquidated damages and penalties. Net payable is gross less the applied amounts; a waived amount is recorded separately with its waiver authority."}
        sample="Retention deductions are what the retention register is built from — the register is their sum, never an independently asserted number." />}>
      <table className="w-full text-[11px]">
        <thead>
          <tr style={{ color: T.muted }}>
            {['Type', 'Basis', 'Rate', 'Computed', 'Applied', 'Waived'].map((h, i) => (
              <th key={h} className={`py-1.5 font-semibold ${i > 1 ? 'text-right px-2' : 'text-left pr-2'}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(d.deductions || []).map((x) => (
            <tr key={x.deduction_id} data-testid={`ipt-deduction-${x.deduction_type}`}
              style={{ borderTop: `1px solid ${T.border}` }}>
              <td className="py-1.5 pr-2" style={{ color: T.ink }}>
                {x.deduction_type}
                {x.debit_note_no && (
                  <span className="block text-[9.5px] font-mono" style={{ color: T.muted }}>
                    {x.debit_note_no} · {x.reference}
                  </span>
                )}
              </td>
              <td className="py-1.5 pr-2" style={{ color: T.body }}>{x.basis}</td>
              <td className="py-1.5 px-2 text-right tabular-nums" style={{ color: T.body }}>
                {x.rate === null ? '—' : `${num(x.rate, 2)}%`}
              </td>
              <td className="py-1.5 px-2 text-right tabular-nums" style={{ color: T.body }}>
                {money(x.computed_amount, cur)}
              </td>
              <td className="py-1.5 px-2 text-right tabular-nums font-semibold"
                style={{ color: T.ink }}>{money(x.applied_amount, cur)}</td>
              <td className="py-1.5 px-2 text-right tabular-nums" style={{ color: T.amber }}>
                {x.waived_amount ? money(x.waived_amount, cur) : '—'}
              </td>
            </tr>
          ))}
          {!(d.deductions || []).length && <tr><td colSpan={6}><Empty T={T}>None applied.</Empty></td></tr>}
        </tbody>
      </table>
      {d.payable && (
        <div className="mt-2 pt-2 text-[10.5px] space-y-1"
          data-testid="ipt-payable-reconcile"
          style={{ borderTop: `1px dashed ${T.border}` }}>
          <div className="flex items-baseline gap-2">
            <span style={{ color: T.muted }}>Gross</span>
            <span className="tabular-nums" style={{ color: T.body }}>
              {money(d.payable.gross, cur)}
            </span>
            <span style={{ color: T.muted }}>− ledger</span>
            <span className="tabular-nums" style={{ color: T.body }}>
              {money(d.payable.ledgerTotal, cur)}
            </span>
            <span style={{ color: T.muted }}>= net payable</span>
            <span className="tabular-nums font-semibold" style={{ color: T.ink }}>
              {money(d.payable.netPayable, cur)}
            </span>
            <span className="ml-auto px-1.5 py-[1px] rounded text-[9px] font-bold uppercase tracking-wide"
              data-testid="ipt-payable-verdict"
              style={d.payable.reconciles
                ? { background: T.tint(T.green, '22'), color: T.green }
                : { background: T.tint(T.amber, '22'), color: T.amber }}>
              {d.payable.reconciles ? 'reconciles' : `${money(d.payable.unitemised, cur)} unitemised`}
            </span>
          </div>
          <Sample T={T} testId="ipt-payable-sample" text={d.payable.statement} />
        </div>
      )}
    </Panel>
    <Panel T={T} testId="ipt-retention-balances" title="Retention balances"
      subtitle="This vendor, this project — the register's own records">
      {(d.retention || []).length ? (d.retention || []).map((r) => (
        <div key={r.retention_id} className="py-2 text-[11px]"
          data-testid={`ipt-retention-${r.contract_id}`}
          style={{ borderTop: `1px solid ${T.border}` }}>
          <div className="flex items-center gap-2">
            <span className="font-semibold" style={{ color: T.ink }}>
              {r.cc_contract_id ? (
                <a href={`/contracts/${r.cc_contract_id}`} className="hover:underline"
                  data-testid={`ipt-det-cc-link-${r.contract_id}`} style={{ color: T.primary }}>
                  {r.contract_id}
                </a>
              ) : r.contract_id}
            </span>
            <span className="text-[10px]" style={{ color: T.muted }}>
              {num(r.retention_pct, 1)}% · release on {r.release_milestone}
            </span>
            <span className="ml-auto tabular-nums font-semibold" style={{ color: T.ink }}>
              {money(r.balance_held, r.currency || cur)}
            </span>
          </div>
          <div className="text-[10px] mt-0.5" style={{ color: T.body }}>
            withheld {money(r.total_withheld, r.currency || cur)} ·
            released {money(r.released_to_date, r.currency || cur)} ·
            due {day(r.release_due_date)}
            {(r.releases || []).length ? ` · ${int((r.releases || []).length)} release event(s)` : ''}
          </div>
        </div>
      )) : <Empty T={T}>No retention held against this vendor on this project.</Empty>}
    </Panel>
  </div>
);

const Payments = ({ d, T, cur, vim }) => (
  <Panel T={T} testId="ipt-payments" title="Payment events"
    subtitle={`${int((d.payments || []).length)} events, oldest first`}>
    <table className="w-full text-[11px]">
      <thead>
        <tr style={{ color: T.muted }}>
          {['Event', 'Value date', 'Amount', 'Document', 'Method', 'Source'].map((h, i) => (
            <th key={h} className={`py-1.5 font-semibold ${i > 1 ? 'text-right px-2' : 'text-left pr-2'}`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {(d.payments || []).map((p) => (
          <tr key={p.event_id} data-testid={`ipt-payment-${p.event_id}`}
            style={{ borderTop: `1px solid ${T.border}` }}>
            <td className="py-1.5 pr-2 font-semibold" style={{ color: T.ink }}>
              {p.event_type}
              {p.reason && (
                <span className="block text-[9.5px] font-normal" style={{ color: T.muted }}>
                  {p.reason}
                </span>
              )}
            </td>
            <td className="py-1.5 pr-2 tabular-nums" style={{ color: T.body }}>{day(p.value_date)}</td>
            <td className="py-1.5 px-2 text-right tabular-nums" style={{ color: T.ink }}>
              {p.amount ? money(p.amount, cur) : '—'}
            </td>
            <td className="py-1.5 px-2 text-right font-mono text-[10px]" style={{ color: T.body }}>
              {p.payment_doc_no || p.clearing_doc_no || p.reference || '—'}
            </td>
            <td className="py-1.5 px-2 text-right" style={{ color: T.body }}>
              {p.payment_method || '—'}
            </td>
            <td className="py-1.5 px-2 text-right" style={{ color: T.muted }}>
              {p.source || '—'}
              {p.source === 'SAP_VIM' && vim?.simulated && (
                <span className="ml-1 px-1 rounded text-[8.5px] font-bold uppercase"
                  style={{ background: T.tint(T.accent, '1A'), color: T.accent }}>sim</span>
              )}
            </td>
          </tr>
        ))}
        {!(d.payments || []).length && <tr><td colSpan={6}><Empty T={T}>No payment events yet.</Empty></td></tr>}
      </tbody>
    </table>
  </Panel>
);

const DOC_CATEGORIES = ['Invoice', 'Measurement sheet', 'GRN / SES', 'Test certificate',
  'Debit note', 'Tax document', 'Correspondence', 'Other'];

const Documents = ({ d, T, onReload }) => {
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(null);
  const [category, setCategory] = useState('Invoice');
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const docs = d.documents || [];
  const { analyse, busy: analysing, jobs } = useAnalyse(d.invoice.invoice_id, onReload);
  const stored = useMemo(() => {
    const out = {};
    ((d.docIntel || {}).jobs || []).forEach((j) => { out[j.document_id] = j; });
    return out;
  }, [d.docIntel]);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of Array.from(files)) {
        const body = new FormData();
        body.append('file', f);
        body.append('category', category);
        // chunk-free but progress-reported, so a 20 MB drawing does not look stalled
        await api.post(`/ipt/invoices/${d.invoice.invoice_id}/documents`, body, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => setPct(e.total
            ? Math.round((100 * e.loaded) / e.total) : null),
        });
        toast.success(`${f.name} attached`);
      }
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload refused');
    } finally { setBusy(false); setPct(null); }
  };

  const rename = async (doc, filename, cat) => {
    try {
      await api.patch(`/ipt/documents/${doc.document_id}`,
        { filename, category: cat });
      toast.success('Document updated');
      setEditing(null); onReload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update'); }
  };

  const download = async (doc) => {
    try {
      const r = await api.get(`/ipt/documents/${doc.document_id}/download`,
        { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = window.document.createElement('a');
      a.href = url; a.download = doc.filename || doc.document_id;
      window.document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { toast.error('Could not download this document'); }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete ${doc.filename}? The audit trail keeps a record of the `
      + 'deletion, including its checksum.')) return;
    try {
      await api.delete(`/ipt/documents/${doc.document_id}`);
      toast.success('Deleted — the audit trail records it');
      onReload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); }
  };

  return (
    <div className="space-y-4">
    <Panel T={T} testId="ipt-documents" title="Documents"
      subtitle={`${int(docs.length)} attached · stored through the module's own storage `
        + 'adapter (GridFS) — no external service'}
      right={(
        <div className="flex items-center gap-2">
          <select value={category} onChange={(e) => setCategory(e.target.value)}
            data-testid="ipt-doc-category"
            className="text-[10.5px] rounded px-2 py-1 outline-none"
            style={{ background: T.surfaceAlt, color: T.ink,
                     border: `1px solid ${T.border}` }}>
            {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <label data-testid="ipt-doc-upload-label"
            className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold cursor-pointer"
            style={{ background: T.primary, color: '#fff' }}>
            <Upload className="w-3 h-3" />
            {busy ? (pct === null ? 'Uploading…' : `${pct}%`) : 'Upload'}
            <input type="file" multiple data-testid="ipt-doc-upload"
              className="absolute w-px h-px opacity-0 overflow-hidden"
              onChange={(e) => upload(e.target.files)} />
          </label>
        </div>
      )}>
      {busy && pct !== null && (
        <div className="h-1 rounded-full mb-2 overflow-hidden" data-testid="ipt-doc-progress"
          style={{ background: T.border }}>
          <div className="h-full" style={{ width: `${pct}%`, background: T.primary }} />
        </div>
      )}
      {docs.length ? docs.map((x) => (
        <div key={x.document_id} className="flex items-center gap-2 py-1.5 text-[11px]"
          data-testid={`ipt-document-${x.document_id}`}
          style={{ borderTop: `1px solid ${T.border}` }}>
          <FileText className="w-3.5 h-3.5 shrink-0" style={{ color: T.primary }} />
          {editing === x.document_id ? (
            <EditRow T={T} doc={x} onSave={rename} onCancel={() => setEditing(null)} />
          ) : (
            <>
              <span className="truncate" style={{ color: T.ink }}>
                {x.filename || x.document_type}
              </span>
              <span className="px-1.5 rounded text-[9px] font-semibold uppercase"
                style={{ background: T.tint(T.primary, '16'), color: T.primary }}>
                {x.category || x.document_type || 'other'}
              </span>
              <span className="text-[9.5px]" style={{ color: T.muted }}>
                {x.size ? `${(x.size / 1024).toFixed(0)} KB` : ''}
                {x.uploaded_at ? ` · ${day(x.uploaded_at)}` : ''}
                {x.uploaded_by ? ` · ${x.uploaded_by}` : ''}
              </span>
              <span className="ml-auto flex items-center gap-2">
                <AnalyseChip doc={x} T={T} onAnalyse={analyse}
                  busy={analysing[x.document_id]}
                  job={jobs[x.document_id] || stored[x.document_id]} />
                <button type="button" className="hover:underline" style={{ color: T.primary }}
                  data-testid={`ipt-doc-view-${x.document_id}`}
                  onClick={() => setViewing(x)}>view</button>
                <button type="button" className="hover:underline" style={{ color: T.primary }}
                  data-testid={`ipt-doc-download-${x.document_id}`}
                  onClick={() => download(x)}>download</button>
                <button type="button" onClick={() => setEditing(x.document_id)}
                  data-testid={`ipt-doc-edit-${x.document_id}`} className="hover:underline"
                  style={{ color: T.muted }}>rename</button>
                <button type="button" onClick={() => remove(x)}
                  data-testid={`ipt-doc-delete-${x.document_id}`} className="hover:underline"
                  style={{ color: T.red }}>delete</button>
              </span>
            </>
          )}
        </div>
      )) : (
        <Empty T={T}>
          No documents attached. Any file type, up to 25 MB each.
        </Empty>
      )}
    </Panel>
    <MatchSuggestions d={d} T={T} onReload={onReload} />
    {viewing && <IptDocViewer doc={viewing} T={T} onClose={() => setViewing(null)} />}
    </div>
  );
};

const EditRow = ({ T, doc, onSave, onCancel }) => {
  const [name, setName] = useState(doc.filename || '');
  const [cat, setCat] = useState(doc.category || 'Invoice');
  return (
    <span className="flex items-center gap-2 flex-1">
      <input value={name} onChange={(e) => setName(e.target.value)}
        data-testid={`ipt-doc-name-${doc.document_id}`}
        className="text-[10.5px] rounded px-2 py-1 outline-none flex-1"
        style={{ background: T.surfaceAlt, color: T.ink, border: `1px solid ${T.border}` }} />
      <select value={cat} onChange={(e) => setCat(e.target.value)}
        data-testid={`ipt-doc-cat-${doc.document_id}`}
        className="text-[10.5px] rounded px-2 py-1 outline-none"
        style={{ background: T.surfaceAlt, color: T.ink, border: `1px solid ${T.border}` }}>
        {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <button type="button" onClick={() => onSave(doc, name, cat)}
        data-testid={`ipt-doc-save-${doc.document_id}`}
        className="text-[10.5px] font-semibold" style={{ color: T.primary }}>save</button>
      <button type="button" onClick={onCancel} className="text-[10.5px]"
        style={{ color: T.muted }}>cancel</button>
    </span>
  );
};

const REFUSAL_ACTIONS = ['STAGE_REFUSED'];

const Audit = ({ d, T, vim }) => {
  const events = d.audit || [];
  const refusals = events.filter((e) => REFUSAL_ACTIONS.includes(e.action)).length;
  return (
    <Panel T={T} testId="ipt-audit" title="Audit trail"
      subtitle={`${int(events.length)} hash-chained events`
        + (refusals ? ` · ${int(refusals)} refused stage attempt${refusals > 1 ? 's' : ''}` : '')}
      right={<Def T={T} testId="ipt-audit-def" title="Audit trail"
        body={"Each event carries the previous event's hash, so a deletion or an edit breaks the chain visibly. Connector-driven events carry the sync run's correlation id, which also appears in the connector console log."}
        sample={'A refused stage attempt writes no transition row — a refusal is not a transition — but it is recorded here with the rule that refused it and the checklist items that failed, so "the gate held this invoice" stays provable.'} />}>
      <ol className="space-y-0">
        {events.map((e, i) => {
          const refused = REFUSAL_ACTIONS.includes(e.action);
          const after = e.after_value ?? e.after;
          const before = e.before_value ?? e.before;
          return (
            <li key={e.eventId || e.event_id || i} className="flex gap-3 py-1.5 text-[11px]"
              data-testid={`ipt-audit-${i}`}
              style={{ borderTop: i ? `1px solid ${T.border}` : 'none',
                       background: refused ? T.tint(T.red, '12') : 'transparent' }}>
              <span className="w-[108px] shrink-0 text-[10px] tabular-nums" style={{ color: T.muted }}>
                {stamp(e.timestamp || e.at || e.created_at)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="font-semibold"
                  style={{ color: refused ? T.red : T.ink }}>{e.action}</span>
                {refused && (
                  <span className="ml-1.5 px-1.5 py-[1px] rounded text-[9px] font-bold uppercase tracking-wide"
                    data-testid={`ipt-audit-refused-${i}`}
                    style={{ background: T.tint(T.red, '22'), color: T.red }}>
                    no transition written
                  </span>
                )}
                <span className="text-[9.5px] ml-1.5" style={{ color: T.muted }}>
                  #{e.seq} · {e.actor_id} · {e.source}
                  {e.source === 'SAP_VIM' && vim?.simulated && ' · simulated'}
                  {e.correlation_id && ` · corr ${e.correlation_id}`}
                </span>
                {refused && after && (
                  <span className="block mt-1 text-[10.5px]" style={{ color: T.body }}>
                    <span style={{ color: T.ink }}>
                      {after.from_stage} → {after.attempted_label || after.attempted_stage}
                    </span>
                    {' refused by '}
                    <span className="font-semibold" style={{ color: T.ink }}>{after.rule}</span>
                    {after.reason ? ` — ${after.reason}` : ''}
                    {!!(after.failing_items || []).length && (
                      <span className="block mt-0.5">
                        {after.failing_items.map((f, j) => (
                          <span key={f.item_code || j} className="block"
                            data-testid={`ipt-audit-failing-${i}-${j}`}>
                            · {f.item_label || f.item_code}
                            <span className="ml-1 text-[9.5px]" style={{ color: T.red }}>
                              {f.result}
                            </span>
                            {f.blocks_release && (
                              <span className="ml-1 text-[9px]" style={{ color: T.muted }}>
                                blocks release
                              </span>
                            )}
                          </span>
                        ))}
                      </span>
                    )}
                  </span>
                )}
                {!refused && (after || before) && (
                  <span className="block text-[9.5px] font-mono break-all" style={{ color: T.body }}>
                    {JSON.stringify(before ? { before, after } : after)}
                  </span>
                )}
              </span>
            </li>
          );
        })}
        {!events.length && <Empty T={T}>No audit events.</Empty>}
      </ol>
    </Panel>
  );
};

/* ── move-stage dialog ───────────────────────────────────────────────── */
const MoveDialog = ({ T, d, meta, state, setState, onDone }) => {
  const [hint, setHint] = useState(null);
  const [busy, setBusy] = useState(false);
  const requires = (meta?.stagesRequiringWaitingOn || ['DISPUTED']).includes(state.to_stage);

  useEffect(() => {
    if (!state.to_stage) { setHint(null); return; }
    api.get(`/ipt/invoices/${d.invoice.invoice_id}/waiting-on-suggestion?to_stage=${state.to_stage}`)
      .then((r) => {
        setHint(r.data);
        setState((s) => ({ ...s, waiting_on: r.data.editable ? r.data.suggestion : '' }));
      })
      .catch(() => setHint(null));
  }, [state.to_stage, d.invoice.invoice_id, setState]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/ipt/invoices/${d.invoice.invoice_id}/transition`, {
        to_stage: state.to_stage,
        reason: state.reason || undefined,
        waiting_on: state.waiting_on || undefined,
        dispute_basis: state.dispute_basis || undefined,
      });
      toast.success(`Moved to ${STAGE_LABEL[state.to_stage] || state.to_stage}`);
      onDone();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : (det?.error || 'The stage machine refused this move'));
      if (det?.why) toast.message(det.why);
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="fixed inset-0 z-[90]" onClick={() => setState(null)}
        style={{ background: 'rgba(15,23,41,0.5)' }} />
      <div className="fixed left-1/2 top-24 -translate-x-1/2 z-[91] w-[min(460px,94vw)] rounded-lg p-4 space-y-3"
        data-testid="ipt-move-dialog"
        style={{ background: T.surface, border: `1px solid ${T.borderStrong}` }}>
        <h3 className="text-[14px] font-bold" style={{ color: T.ink }}>Move stage</h3>
        <Field label="Next stage" T={T}>
          <Select T={T} value={state.to_stage} data-testid="ipt-move-stage"
            onChange={(e) => setState({ ...state, to_stage: e.target.value })}>
            <option value="">select…</option>
            {(d.nextStages || []).map((s) => (
              <option key={s.stage} value={s.stage}>
                {STAGE_LABEL[s.stage] || s.stage}{s.isBranch ? ' (branch)' : ''}
              </option>
            ))}
          </Select>
        </Field>

        {state.to_stage && hint && (
          <div className="rounded-md px-2.5 py-2 text-[10.5px]"
            data-testid="ipt-waiting-hint"
            style={{ background: T.surfaceAlt, border: `1px solid ${T.border}`, color: T.body }}>
            <b style={{ color: T.ink }}>
              {requires ? 'Who must act next?' : 'Attribution'}
            </b>{' '}
            {hint.rationale}
          </div>
        )}

        {requires && (
          <>
            <Field label="Waiting on — required" T={T}
              hint="Who raised the dispute is not who must act next. State the party.">
              <Select T={T} value={state.waiting_on} data-testid="ipt-move-waiting-on"
                onChange={(e) => setState({ ...state, waiting_on: e.target.value })}>
                <option value="">select…</option>
                {(meta?.waitingOn || []).map((w) => (
                  <option key={w.value} value={w.value}>{w.label}</option>
                ))}
              </Select>
            </Field>
            <Field label="Dispute basis — required" T={T}
              hint="Attribution without a reason is unarguable in the meeting where it will be argued.">
              <Input T={T} value={state.dispute_basis} data-testid="ipt-move-dispute-basis"
                placeholder="e.g. quantity disputed against the JMS — vendor to re-measure"
                onChange={(e) => setState({ ...state, dispute_basis: e.target.value })} />
            </Field>
          </>
        )}

        <Field label="Reason (optional)" T={T}>
          <Input T={T} value={state.reason} data-testid="ipt-move-reason"
            onChange={(e) => setState({ ...state, reason: e.target.value })} />
        </Field>

        <div className="flex items-center gap-2 pt-1">
          <Btn T={T} onClick={submit} testId="ipt-move-submit"
            disabled={busy || !state.to_stage
              || (requires && (!state.waiting_on || !state.dispute_basis.trim()))}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Gavel className="w-3.5 h-3.5" />}
            Record the move
          </Btn>
          <Btn T={T} variant="ghost" onClick={() => setState(null)} testId="ipt-move-cancel">
            Cancel
          </Btn>
          <span className="ml-auto text-[9.5px] flex items-center gap-1" style={{ color: T.muted }}>
            <Clock className="w-3 h-3" /> timestamped now
          </span>
        </div>
      </div>
    </>
  );
};

const KV = ({ k, v, T, tone, bold }) => (
  <div className="flex items-baseline gap-2">
    <span style={{ color: T.muted }}>{k}</span>
    <span className={`ml-auto tabular-nums ${bold ? 'font-bold' : ''}`}
      style={{ color: tone || T.ink }}>{v ?? '—'}</span>
  </div>
);

export default IptDetail;
